/**
 * The `{{ }}` reference syntax in a template string, split into segments and
 * written back. The renderers (`processText` / `templateString` here, and the
 * Rust engine's `process_text`) read the same syntax, so the editor and the
 * server agree on what a reference is:
 *
 *   {{ key }}                                 key: an attribute's id or description
 *   {{ key | uppercase }}                     one formatter
 *   {{ key | date MMMM yyyy | uppercase }}    any number, applied left to right
 *   {{ key | date d MMMM yyyy as "Expiry" }}  an editor-only display label
 *
 * `as "label"` is for authors only: the editor shows the label in place of the
 * attribute's name. Every renderer strips it before resolving the key or
 * applying formatters (`splitTemplateLabel`), so it never changes the output. A
 * label can't contain `"`, `{` or `}`.
 *
 * Key characters match the renderers' expression regexes (`TEMPLATE_KEY_CHARS`),
 * so descriptions with spaces, `@parent/...` paths and the like all parse.
 * `=`, `?` and `!` aren't key characters, so conditional expressions never
 * parse as a segment and stay as text.
 */

import { formatFormatterChain, parseFormatterChain } from "./Formatters";

/** The characters a template key can contain, as a regex character class. Shared with TemplateUtils' regexes. */
export const TEMPLATE_KEY_CHARS = String.raw`[\w\.\[\]'"\s\-\/$@():%]`;

/** A trailing ` as "label"` on the inside of a `{{ }}` reference. */
const TEMPLATE_LABEL_REGEX = /\s+as\s+"([^"{}]*)"\s*$/;

const TEMPLATE_SEGMENT_REGEX = new RegExp(
  String.raw`\{\{\s*(${TEMPLATE_KEY_CHARS}+?)\s*(?:\|\s*([^{}]*?))?(?:\s+as\s+"([^"{}]*)")?\s*\}\}`,
  "g",
);

const TEMPLATE_REFERENCE_REGEX = /\{\{([^{}]*)\}\}/g;

/** Literal text between references. */
export interface TemplateTextSegment {
  kind: "text";
  value: string;
}

/** One `{{ key | formatters as "label" }}` reference. */
export interface TemplateAttributeSegment {
  kind: "attribute";
  /** The attribute key, as `resolveKey` returned it (with any `@parent/...` alias removed). */
  key: string;
  /** Formatters applied in order, as in `{{ key | a | b }}`. Empty for the raw value. */
  formatters: string[];
  /** Editor-only display label from `as "label"`. Renderers ignore it. */
  label?: string;
  /**
   * The deprecated `@parent/...` key as written, when the alias was removed on parse.
   * Not serialized: the segment writes its bare `key`, so saving completes the migration.
   */
  migratedFrom?: string;
}

export type TemplateSegment = TemplateTextSegment | TemplateAttributeSegment;

export interface ParseTemplateSegmentsOptions {
  /**
   * Maps a key as written (alias already removed) to the key to store, or undefined
   * when it names no attribute. An unresolved reference is kept verbatim as text so
   * nothing is silently dropped. Defaults to accepting every key as written.
   */
  resolveKey?: (key: string) => string | undefined;
}

/**
 * Strip the legacy `@parent/<entity>/` (or `@parent/global/`) prefix from a template key.
 *
 * Ancestor attributes now resolve by bare name through the context's ancestry, so the prefix
 * carries no information. It is still accepted so existing templates keep rendering.
 */
export const stripParentAlias = (key: string): string => {
  if (!key.startsWith("@parent/")) return key;
  let rest = key.slice("@parent/".length);
  while (rest.startsWith("@parent/")) rest = rest.slice("@parent/".length);
  // Drop the entity segment, if there is one: `employees/the employee first name`
  const slash = rest.indexOf("/");
  return slash === -1 ? rest : rest.slice(slash + 1);
};

/**
 * Splits the inside of a `{{ }}` reference into the expression a renderer reads and the
 * editor-only label: `name | uppercase as "Name"` -> `name | uppercase` and `Name`.
 */
export const splitTemplateLabel = (inner: string): { expression: string; label?: string } => {
  const match = inner.match(TEMPLATE_LABEL_REGEX);
  if (!match || match.index === undefined) return { expression: inner };
  return { expression: inner.slice(0, match.index), label: match[1] };
};

/** The template with every reference's `as "label"` removed, for renderers that match whole references. */
export const stripTemplateLabels = (template: string): string =>
  template.replace(TEMPLATE_REFERENCE_REGEX, (full, inner: string) => {
    const { expression, label } = splitTemplateLabel(inner);
    return label === undefined ? full : `{{${expression} }}`;
  });

/**
 * Splits a template string into text and attribute segments. Formatter chains are split
 * like the renderers split them, and a deprecated `@parent/...` key is reduced to its
 * attribute (recorded in `migratedFrom`). Returns no segments for an empty string.
 */
export const parseTemplateSegments = (
  template: string,
  { resolveKey = (key) => key }: ParseTemplateSegmentsOptions = {},
): TemplateSegment[] => {
  const segments: TemplateSegment[] = [];
  const pushText = (value: string) => {
    const previous = segments[segments.length - 1];
    if (previous?.kind === "text") previous.value += value;
    else segments.push({ kind: "text", value });
  };

  let lastIndex = 0;
  for (const match of template.matchAll(TEMPLATE_SEGMENT_REGEX)) {
    const [full, writtenKey, chain, label] = match;
    const index = match.index ?? 0;
    if (index > lastIndex) pushText(template.slice(lastIndex, index));
    lastIndex = index + full.length;

    const trimmedKey = writtenKey.trim();
    const bareKey = stripParentAlias(trimmedKey);
    const key = resolveKey(bareKey);
    if (key === undefined) {
      pushText(full);
      continue;
    }
    segments.push({
      kind: "attribute",
      key,
      formatters: parseFormatterChain(chain),
      ...(label !== undefined ? { label } : {}),
      ...(bareKey !== trimmedKey ? { migratedFrom: trimmedKey } : {}),
    });
  }
  if (lastIndex < template.length) pushText(template.slice(lastIndex));
  return segments;
};

/** One attribute segment back to `{{ key | a | b as "label" }}`. An empty label is left out. */
export const serializeTemplateSegment = ({
  key,
  formatters,
  label,
}: Omit<TemplateAttributeSegment, "kind">): string => {
  const chain = formatFormatterChain(formatters);
  return `{{ ${key}${chain ? ` | ${chain}` : ""}${label ? ` as "${label}"` : ""} }}`;
};

/** Segments back to a template string, the inverse of `parseTemplateSegments`. */
export const serializeTemplateSegments = (segments: TemplateSegment[]): string =>
  segments.map((segment) => (segment.kind === "text" ? segment.value : serializeTemplateSegment(segment))).join("");
